import { Bento } from "../components/Bento";
import { t } from "@/i18n-js/instance";
import EventChart from "./assets/events-hosted.png";
import { SanitizeHTMLString } from "@/react/components/shared/uikit/SanitizeContent";
import { abbreviateNumber } from "@/react/helpers/number";
const i18nRoot = "eoy_recap_2025.sections.engagement";

import type { EngagementMetrics } from "../../types";
import { TopPerformingPostCard } from "./TopPerformingPostCard";
import { MostWatchedOrAttendedEvent } from "./MostWatchedOrAttendedEvent";

interface EngagementProps {
  data?: EngagementMetrics;
}

export const Engagement = ({ data }: EngagementProps) => {
  if (!data) return null;

  return (
    <Bento
      id="content-and-experience"
      accentColor="#984290"
      title={t([i18nRoot, "title"])}
    >
      <Bento.Item id="total-posts" baseColSpan={1} baseRowSpan={3}>
        <div className="flex flex-col h-full p-8">
          <p className="text-4xl md:text-6xl font-bold mb-2">
            {abbreviateNumber(data.total_events_hosted)}
          </p>
          <p className="text-base font-medium leading-none tracking-[-0.25px] text-white mb-4">
            {t([i18nRoot, "bento_items.total_events_hosted"])}
          </p>
          <div className="flex-1 min-h-0 mt-4">
            <img
              src={EventChart}
              alt={t([i18nRoot, "bento_items.total_events_hosted"])}
              className="size-full"
              loading="lazy"
            />
          </div>
        </div>
      </Bento.Item>
      <Bento.Item id="total-courses-launched" baseColSpan={1} baseRowSpan={3}>
        <div className="flex flex-col h-full p-8">
          <p className="text-4xl font-bold mb-2 text-center leading-8">
            <SanitizeHTMLString
              content={t([i18nRoot, "bento_items.total_courses_launched"])}
            />
          </p>

          <div className="flex-1 min-h-0 flex items-center justify-center">
            <p className="text-[14rem] font-bold leading-[100%] tracking-[-8.135px] uppercase text-[#EC64CF] mt-4">
              {abbreviateNumber(data.total_courses_launched)}
            </p>
          </div>
        </div>
      </Bento.Item>
      <Bento.Item
        id="top-performing-post-or-content-piece"
        baseColSpan={1}
        baseRowSpan={3}
      >
        <div className="flex flex-col h-full p-8">
          <p className="text-4xl font-bold mb-2 text-center leading-8">
            <SanitizeHTMLString
              content={t([
                i18nRoot,
                "bento_items.top_performing_post_or_content_piece",
              ])}
            />
          </p>

          <div className="flex-1 min-h-0 flex items-center justify-center">
            <TopPerformingPostCard post={data.top_performing_post} />
          </div>
        </div>
      </Bento.Item>
      <Bento.Item
        id="most-watched-or-attended-event"
        baseColSpan={2}
        baseRowSpan={2}
      >
        <div className="flex flex-col lg:flex-row items-center lg:justify-between size-full p-8 gap-4 lg:gap-0">
          <p className="text-4xl font-bold text-center leading-8 self-start">
            <SanitizeHTMLString
              content={t([
                i18nRoot,
                "bento_items.most_watched_or_attended_event",
              ])}
            />
          </p>

          <MostWatchedOrAttendedEvent event={data.most_attended_event} />
        </div>
      </Bento.Item>
      <Bento.Item
        id="most-discussed-course-or-topic"
        baseColSpan={1}
        baseRowSpan={2}
      >
        <div className="px-8 pt-8">
          <p className="text-4xl font-bold mb-2 text-center leading-8">
            <SanitizeHTMLString
              content={t([
                i18nRoot,
                "bento_items.most_discussed_course_or_topic",
              ])}
            />
          </p>
        </div>
        <div className="flex flex-row gap-4 mt-16 overflow-hidden justify-center">
          <div className="px-8 py-3 bg-[#49335A] rounded-full w-[200px] shrink-0" />
          <div className="px-8 py-3 bg-[#EC64CF] rounded-full flex items-center justify-center shrink-0 min-w-fit">
            <p className="text-base font-bold text-white whitespace-nowrap">
              {data.most_discussed_topic}
            </p>
          </div>
          <div className="px-8 py-3 bg-[#49335A] rounded-full w-[200px] shrink-0" />
        </div>
      </Bento.Item>
    </Bento>
  );
};
